import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Users,
  UserCheck,
  Briefcase,
  Settings,
  AlertCircle,
  Shield,
  ChevronRight,
} from 'lucide-react';
import Header from '../components/Header';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();

  if (!isAdmin()) {
    return (
      <div>
        <Header title="Administration" />
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center border border-gray-200">
            <div className="w-16 h-16 bg-red-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-10 h-10 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Accès refusé</h2>
            <p className="text-gray-600 mb-6">
              Vous n'avez pas les permissions nécessaires pour accéder à cette page.
            </p>
            <button
              onClick={() => navigate('/')}
              className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              Retour à l'accueil
            </button>
          </div>
        </div>
      </div>
    );
  }

  const adminCards = [
    {
      title: 'Liste des Membres',
      description: 'Gérer tous les utilisateurs actifs, modifier leurs informations et rôles',
      icon: Users,
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-600',
      borderColor: 'border-blue-200',
      path: '/admin/members',
      badge: 'Gestion complète',
    },
    {
      title: 'Utilisateurs en attente',
      description: 'Valider ou refuser les nouvelles demandes d\'inscription',
      icon: UserCheck,
      iconBg: 'bg-yellow-100',
      iconColor: 'text-yellow-600',
      borderColor: 'border-yellow-200',
      path: '/admin/pending-users',
      badge: 'Validation',
    },
    {
      title: 'Gestion des Commissions',
      description: 'Créer, modifier et supprimer les commissions du dahira',
      icon: Briefcase,
      iconBg: 'bg-purple-100',
      iconColor: 'text-purple-600',
      borderColor: 'border-purple-200',
      path: '/admin/commissions',
      badge: 'Gestion complète',
    },
    {
      title: 'Profil',
      description: 'Configurer les informations de votre profil',
      icon: Settings,
      iconBg: 'bg-gray-100',
      iconColor: 'text-gray-600',
      borderColor: 'border-gray-200',
      path: '/settings',
      badge: 'Configuration',
    },
  ];

  // Toutes les cartes sont accessibles aux admins
  const filteredCards = adminCards;

  return (
    <div>
      <Header title="Administration" />

      <div className="min-h-screen bg-gray-50 p-4 md:p-8">
        <div className="max-w-7xl mx-auto">
          {/* En-tête */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center">
                <Shield className="text-primary-600" size={24} />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">
                  Administration
                </h1>
                <p className="text-gray-600">
                  Bienvenue {user?.firstName}, gérez votre application
                </p>
              </div>
            </div>

            {/* Badge de rôle */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg">
              <Shield className="text-purple-600" size={18} />
              <span className="text-sm font-semibold text-purple-900">
                Administrateur
              </span>
            </div>
          </div>

          {/* Grille des fonctionnalités */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
            {filteredCards.map((card, index) => {
              const Icon = card.icon;
              return (
                <div
                  key={index}
                  onClick={() => navigate(card.path)}
                  className={`group bg-white rounded-2xl shadow-sm border-2 ${card.borderColor} p-6 hover:shadow-xl transition-all duration-300 cursor-pointer hover:-translate-y-1`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className={`w-14 h-14 ${card.iconBg} rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform`}>
                      <Icon className={card.iconColor} size={28} />
                    </div>
                    <div className="flex items-center gap-2 text-gray-400 group-hover:text-primary-600 transition-colors">
                      <span className="text-xs font-medium">{card.badge}</span>
                      <ChevronRight size={20} />
                    </div>
                  </div>

                  <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-primary-600 transition-colors">
                    {card.title}
                  </h3>

                  <p className="text-sm text-gray-600 leading-relaxed">
                    {card.description}
                  </p>
                </div>
              );
            })}
          </div>

          {/* Section d'information */}
          <div className="mt-8 bg-gradient-to-r from-primary-600 to-primary-800 rounded-2xl p-6 md:p-8 text-white">
            <h3 className="text-xl font-bold mb-2">Fonctionnalités disponibles</h3>
            <ul className="space-y-2 text-primary-100">
              <li className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
                <span>Gestion complète des utilisateurs (ajout, modification, suppression)</span>
              </li>
              <li className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
                <span>Changement de rôles et permissions</span>
              </li>
              <li className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
                <span>Gestion des commissions</span>
              </li>
              <li className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
                <span>Validation des nouveaux utilisateurs</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
